/**
 * FDV — velocidad de carga (`fdv_web_vitals`).
 *
 * Un evento por página, con los valores finales, al ocultarse o cerrarse la página (LCP y CLS no
 * son definitivos antes). Muestra del 20% de los navegadores, decidida por un hash estable de
 * `FDV.anonId`: un navegador entra o sale en todas sus páginas y conserva su recorrido completo.
 *
 * Sin librerías: `PerformanceObserver`. Cada métrica va `null` si el navegador no la soporta
 * (INP y LCP no los dan todos; se mide, no se supone). Sin datos personales.
 *
 * Especificación: flor_venus_comercial_intelligence,
 * 2026/10-octubre/campanas/2026-10-07_alcance-y-captacion/eventos-nuevos.md (§4).
 */
(() => {
  const FDV = window.FDV;
  if (!FDV || typeof FDV.track !== 'function' || FDV.vitals) return;

  const V = 1;
  const SAMPLE_PCT = 20;
  const MAX_INTERACTIONS = 1000;

  let debug = false;
  try {
    debug = localStorage.getItem('fdv_debug') === '1';
  } catch (e) {
    /* noop */
  }

  // Hash FNV-1a de 32 bits: estable por navegador.
  function bucket(id) {
    let h = 0x811c9dc5;
    for (let i = 0; i < id.length; i++) {
      h ^= id.charCodeAt(i);
      h = Math.imul(h, 0x01000193);
    }
    return (h >>> 0) % 100;
  }
  if (!debug && bucket(String(FDV.anonId || '')) >= SAMPLE_PCT) return;

  FDV.vitals = { v: V };

  let lcpEntry = null;
  let lcpDone = false;
  let clsMax = 0;
  let clsSession = 0;
  let clsFirst = 0;
  let clsLast = 0;
  const interactions = new Map(); // interactionId → { dur, target }
  let sent = false;
  const observers = [];

  function observe(type, cb, opts) {
    try {
      if (!PerformanceObserver.supportedEntryTypes?.includes(type)) return false;
      const po = new PerformanceObserver((list) => list.getEntries().forEach(cb));
      po.observe({ type, buffered: true, ...opts });
      observers.push(po);
      return true;
    } catch (e) {
      return false;
    }
  }

  const hasLcp = observe('largest-contentful-paint', (e) => {
    if (!lcpDone) lcpEntry = e;
  });

  const hasCls = observe('layout-shift', (e) => {
    if (e.hadRecentInput) return;
    // Ventanas de sesión de la métrica: hueco < 1 s y duración < 5 s; se toma la mayor.
    if (clsSession && e.startTime - clsLast < 1000 && e.startTime - clsFirst < 5000) {
      clsSession += e.value;
    } else {
      clsSession = e.value;
      clsFirst = e.startTime;
    }
    clsLast = e.startTime;
    if (clsSession > clsMax) clsMax = clsSession;
  });

  const hasInp = observe(
    'event',
    (e) => {
      if (!e.interactionId) return;
      const prev = interactions.get(e.interactionId);
      if (prev) {
        if (e.duration > prev.dur) {
          prev.dur = e.duration;
          prev.target = e.target;
        }
      } else if (interactions.size < MAX_INTERACTIONS) {
        interactions.set(e.interactionId, { dur: e.duration, target: e.target });
      }
    },
    { durationThreshold: 40 }
  );

  // El LCP deja de actualizarse con la primera interacción de la persona.
  const stopLcp = () => {
    lcpDone = true;
  };
  for (const type of ['keydown', 'pointerdown']) {
    addEventListener(type, stopLcp, { once: true, capture: true, passive: true });
  }

  function recurso(entry) {
    if (!entry.url) return null;
    if (entry.url.startsWith('data:')) return 'data';
    try {
      return new URL(entry.url, location.href).pathname.slice(0, 80);
    } catch (e) {
      return null;
    }
  }

  function lcpTipo(entry) {
    const tag = entry.element?.tagName;
    if (tag === 'VIDEO') return 'video';
    if (tag === 'IMG' || entry.url) return 'img';
    return 'texto';
  }

  function objetivo(el) {
    if (!el || !el.tagName) return null;
    const cls = el.classList?.[0];
    return (el.tagName.toLowerCase() + (cls ? `.${cls}` : '')).slice(0, 60);
  }

  function inp() {
    if (!hasInp || !interactions.size) return null;
    // Percentil 98 de las interacciones; con menos de 50, la más lenta.
    const sorted = [...interactions.values()].sort((a, b) => b.dur - a.dur);
    return sorted[Math.min(sorted.length - 1, Math.floor(sorted.length / 50))];
  }

  function send() {
    if (sent) return;
    sent = true;
    if (FDV.consent?.analytics === false) return;
    observers.forEach((o) => {
      try {
        o.takeRecords?.();
        o.disconnect();
      } catch (e) {
        /* noop */
      }
    });

    const nav = performance.getEntriesByType('navigation')[0];
    const fcp = performance.getEntriesByName('first-contentful-paint')[0];
    const worst = inp();
    const conn = navigator.connection;
    const ttfb = nav ? Math.round(nav.responseStart) : null;

    const props = {
      lcp_ms: hasLcp && lcpEntry ? Math.round(lcpEntry.startTime) : null,
      lcp_tipo: hasLcp && lcpEntry ? lcpTipo(lcpEntry) : null,
      lcp_recurso: hasLcp && lcpEntry ? recurso(lcpEntry) : null,
      inp_ms: worst ? Math.round(worst.dur) : null,
      inp_objetivo: worst ? objetivo(worst.target) : null,
      cls_x1000: hasCls ? Math.round(clsMax * 1000) : null,
      fcp_ms: fcp ? Math.round(fcp.startTime) : null,
      ttfb_ms: ttfb,
      nav_tipo: document.prerendering || nav?.activationStart > 0 ? 'prerender' : nav?.type || null,
      conexion: conn?.effectiveType ?? null,
      ahorro_datos: typeof conn?.saveData === 'boolean' ? conn.saveData : null,
      memoria_gb: typeof navigator.deviceMemory === 'number' ? navigator.deviceMemory : null,
      muestra: SAMPLE_PCT,
      device: matchMedia('(max-width: 749px)').matches || 'ontouchstart' in window ? 'mobile' : 'desktop',
      v: V,
    };
    if (props.lcp_ms == null && props.fcp_ms == null && props.ttfb_ms == null) return;
    try {
      FDV.track('fdv_web_vitals', props);
    } catch (e) {
      /* el tracking nunca debe romper la página */
    }
  }

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') send();
  });
  addEventListener('pagehide', send);
})();
